/**
 * PDF Text Extraction Utility with OCR Support
 * 
 * Extracts text from PDF files with page-level granularity.
 * Falls back to OCR (Tesseract.js) for scanned/image PDFs.
 */

import * as pdfjsLib from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import Tesseract from 'tesseract.js';

// Configure worker for PDF.js
pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

// Type for PDF extraction result
export interface PDFExtractResult {
    text: string;
    pages: PDFPage[];
    metadata: PDFMetadata;
    usedOCR: boolean;
}

export interface PDFPage {
    pageNumber: number;
    content: string;
}

export interface PDFMetadata {
    title?: string;
    author?: string;
    pageCount: number;
    creationDate?: Date;
}

// Minimum characters per page to consider text extraction successful
const MIN_CHARS_PER_PAGE = 50;

/**
 * Extracts text content from a PDF file
 * Falls back to OCR if text extraction yields minimal content
 */
export async function extractTextFromPDF(
    file: File,
    onProgress?: (progress: number, status: string) => void
): Promise<PDFExtractResult> {
    const arrayBuffer = await file.arrayBuffer();

    onProgress?.(5, 'Loading PDF...');

    // Load PDF document
    const loadingTask = pdfjsLib.getDocument({ data: arrayBuffer });
    const pdf = await loadingTask.promise;

    onProgress?.(10, 'Extracting text...');

    // First try text extraction
    const textResult = await extractTextFromPages(pdf, onProgress);

    // Check if we got meaningful text content
    const avgCharsPerPage = textResult.text.length / pdf.numPages;

    if (avgCharsPerPage >= MIN_CHARS_PER_PAGE) {
        // Text extraction was successful
        console.log('PDF text extraction successful:', avgCharsPerPage, 'chars/page');

        // Get metadata
        let metadata: any = null;
        try {
            metadata = await pdf.getMetadata();
        } catch (e) { /* ignore */ }

        return {
            text: textResult.text,
            pages: textResult.pages,
            metadata: {
                title: metadata?.info?.Title || file.name.replace('.pdf', ''),
                author: metadata?.info?.Author,
                pageCount: pdf.numPages,
                creationDate: metadata?.info?.CreationDate
                    ? new Date(metadata.info.CreationDate)
                    : undefined
            },
            usedOCR: false
        };
    }

    // Text extraction failed or yielded minimal content - use OCR
    console.log('Text extraction yielded minimal content, falling back to OCR');
    onProgress?.(20, 'Scanned PDF detected. Running OCR...');

    return await extractWithOCR(pdf, file, onProgress);
}

/**
 * Extract text from PDF pages using PDF.js text layer
 */
async function extractTextFromPages(
    pdf: pdfjsLib.PDFDocumentProxy,
    onProgress?: (progress: number, status: string) => void
): Promise<{ text: string; pages: PDFPage[] }> {
    const pages: PDFPage[] = [];
    let fullText = '';

    for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();

        const pageText = textContent.items
            .map((item: any) => item.str)
            .join(' ')
            .replace(/\s+/g, ' ')
            .trim();

        pages.push({
            pageNumber: i,
            content: pageText
        });

        fullText += pageText + '\n\n';

        const progress = 10 + (i / pdf.numPages) * 10;
        onProgress?.(progress, `Extracting page ${i}/${pdf.numPages}...`);
    }

    return { text: fullText.trim(), pages: pages.filter(p => p.content.length > 0) };
}

/**
 * Extract text using OCR (Tesseract.js)
 * Converts PDF pages to images then runs OCR
 */
async function extractWithOCR(
    pdf: pdfjsLib.PDFDocumentProxy,
    file: File,
    onProgress?: (progress: number, status: string) => void
): Promise<PDFExtractResult> {
    const pages: PDFPage[] = [];
    let fullText = '';

    // Initialize Tesseract worker
    const worker = await Tesseract.createWorker('eng', 1, {
        logger: progress => {
            if (progress.status === 'recognizing text') {
                const percent = Math.round(progress.progress * 100);
                console.log(`OCR progress: ${percent}%`);
            }
        }
    });

    try {
        for (let i = 1; i <= pdf.numPages; i++) {
            onProgress?.(
                20 + ((i - 1) / pdf.numPages) * 70,
                `OCR processing page ${i}/${pdf.numPages}...`
            );

            // Get page and render to canvas
            const page = await pdf.getPage(i);
            const viewport = page.getViewport({ scale: 2.0 }); // Higher scale = better OCR

            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            if (!context) throw new Error('Could not create canvas context');

            canvas.height = viewport.height;
            canvas.width = viewport.width;

            await (page.render({
                canvasContext: context,
                viewport: viewport
            } as any)).promise;

            // Convert canvas to blob and run OCR
            const blob = await new Promise<Blob>((resolve) => {
                canvas.toBlob((b) => resolve(b!), 'image/png');
            });

            const result = await worker.recognize(blob);
            const pageText = result.data.text.trim();

            pages.push({
                pageNumber: i,
                content: pageText
            });

            fullText += pageText + '\n\n';

            console.log(`OCR Page ${i}: ${pageText.length} chars extracted`);
        }
    } finally {
        await worker.terminate();
    }

    onProgress?.(95, 'Finalizing...');

    // Get metadata
    let metadata: any = null;
    try {
        metadata = await pdf.getMetadata();
    } catch (e) { /* ignore */ }

    return {
        text: fullText.trim(),
        pages: pages.filter(p => p.content.length > 0),
        metadata: {
            title: metadata?.info?.Title || file.name.replace('.pdf', ''),
            author: metadata?.info?.Author,
            pageCount: pdf.numPages,
            creationDate: metadata?.info?.CreationDate
                ? new Date(metadata.info.CreationDate)
                : undefined
        },
        usedOCR: true
    };
}

/**
 * Chunks text into smaller segments for context window management
 */
export function chunkText(
    text: string,
    maxChunkSize: number = 3000,
    overlap: number = 200
): string[] {
    const chunks: string[] = [];
    const paragraphs = text.split(/\n\n+/);
    let currentChunk = '';

    for (const paragraph of paragraphs) {
        if (currentChunk.length + paragraph.length > maxChunkSize && currentChunk.length > 0) {
            chunks.push(currentChunk.trim());
            const overlapText = currentChunk.slice(-overlap);
            currentChunk = overlapText + '\n\n' + paragraph;
        } else {
            currentChunk += (currentChunk ? '\n\n' : '') + paragraph;
        }
    }

    if (currentChunk.trim()) {
        chunks.push(currentChunk.trim());
    }

    return chunks;
}

/**
 * Formats extracted content for use in prompts with page references
 */
export function formatContentForPrompt(result: PDFExtractResult): string {
    let formatted = '';

    for (const page of result.pages) {
        if (page.content.trim()) {
            formatted += `\n--- PAGE ${page.pageNumber} ---\n`;
            formatted += page.content;
            formatted += '\n';
        }
    }

    return formatted.trim();
}

/**
 * Validates that a PDF file meets requirements
 */
export function validatePDFFile(file: File): { valid: boolean; error?: string } {
    if (!file.type.includes('pdf') && !file.name.toLowerCase().endsWith('.pdf')) {
        return { valid: false, error: 'File must be a PDF document' };
    }

    const maxSize = 15 * 1024 * 1024; // 15MB for OCR
    if (file.size > maxSize) {
        return { valid: false, error: 'PDF file must be under 15MB' };
    }

    if (file.size === 0) {
        return { valid: false, error: 'PDF file is empty' };
    }

    return { valid: true };
}
